import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthTokenService } from '../Modules/Shared/Services/auth-token.service';

@Injectable({
  providedIn: 'root'
})
export class ImpersonationFacade {
  private readonly _authTokenService = inject(AuthTokenService);
  private readonly _router = inject(Router);

  private readonly EXPIRED_FLAG_KEY = 'impersonation_expired';

  private readonly _active = signal(false);
  private readonly _impersonatorEmail = signal<string | null>(null);
  private readonly _remainingSeconds = signal(0);

  private _tickInterval: ReturnType<typeof setInterval> | null = null;
  private _sessionEnded = false;

  readonly active = this._active.asReadonly();
  readonly impersonatorEmail = this._impersonatorEmail.asReadonly();
  readonly remainingSeconds = this._remainingSeconds.asReadonly();

  readonly warning = computed(() => this._active() && this._remainingSeconds() <= 60);

  readonly counterLabel = computed(() => {
    const total = this._remainingSeconds();
    const minutes = Math.floor(total / 60).toString().padStart(2, '0');
    const seconds = (total % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  });

  constructor() {
    this.refreshFromToken();
    this.registerTickWhileActive();
  }

  refreshFromToken(): void {
    if (!this._authTokenService.isImpersonated()) {
      this._active.set(false);
      this._impersonatorEmail.set(null);
      this._remainingSeconds.set(0);
      return;
    }

    const remaining = this.computeRemainingSeconds();

    this._sessionEnded = false;
    this._impersonatorEmail.set(this._authTokenService.getImpersonatorEmail());
    this._remainingSeconds.set(remaining);
    this._active.set(remaining > 0);
  }

  endSession(): void {
    if (this._sessionEnded) {
      return;
    }

    this._sessionEnded = true;
    sessionStorage.setItem(this.EXPIRED_FLAG_KEY, this._impersonatorEmail() ?? '1');
    this._authTokenService.clearToken();
    this._active.set(false);
    this._router.navigate(['/auth/impersonation-expired']);
  }

  private registerTickWhileActive(): void {
    effect(() => {
      if (this._active()) {
        this.startTick();
      } else {
        this.stopTick();
      }
    });
  }

  private startTick(): void {
    if (this._tickInterval !== null) {
      return;
    }

    this._tickInterval = setInterval(() => this.tick(), 1000);
  }

  private stopTick(): void {
    if (this._tickInterval !== null) {
      clearInterval(this._tickInterval);
      this._tickInterval = null;
    }
  }

  private tick(): void {
    const remaining = this.computeRemainingSeconds();

    this._remainingSeconds.set(remaining);

    if (remaining <= 0) {
      this.endSession();
    }
  }

  private computeRemainingSeconds(): number {
    const exp = this._authTokenService.getTokenExp();

    if (exp === null) {
      return 0;
    }

    return Math.max(0, Math.floor((exp * 1000 - Date.now()) / 1000));
  }
}
